import { EstimatorAvatar, makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import classnames from 'classnames'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import LocationOnIcon from '@material-ui/icons/LocationOnOutlined'
import PeopleIcon from '@material-ui/icons/PeopleAltOutlined'
import PersonIcon from '@material-ui/icons/PersonOutline'
import React from 'react'
import { getActivityColor } from '@paintscout/util'
import { activatable } from './activatable'
import { useQuoteDocumentHeaderStyles } from './quoteDocumentHeaderStyles'
import type { QuoteDocumentHeaderSection } from './QuoteDocumentHeaderMobileSheet'
import type { QuoteDocumentHeaderData } from './useQuoteDocumentHeaderData'

// The summary row's caret and a work order's "Issued:" date, from the prototype's summary row (ENG-2080).
const useSummaryStyles = makeStyles(
  (theme: Theme) => ({
    summaryCaret: { fontSize: 18, marginRight: -2, color: theme.paintscout.palette.brand.Neutral.P50 },
    summaryIssued: { color: theme.paintscout.palette.brand.Neutral.P50 }
  }),
  { name: 'QuoteDocumentHeaderMobileSummary' }
)

/** The compact rows a template shows as inactive placeholders — see the desktop header's note. */
const TEMPLATE_ROWS = [
  { key: 'contact', icon: PersonIcon, label: 'Prepared for' },
  { key: 'job-address', icon: LocationOnIcon, label: 'Job Address' },
  { key: 'estimator', icon: PeopleIcon, label: 'Prepared by' }
]

export interface QuoteDocumentHeaderMobileProps {
  /** Render without the card's own border/rounding/gap — for embedding in a shared card. */
  flush?: boolean
  data: QuoteDocumentHeaderData
  /** Open the detail sheet for a row that already has a value. */
  onOpenSection: (section: QuoteDocumentHeaderSection) => void
}

/**
 * The phone document header. Phones never render the desktop layout: a washed summary row (doc label /
 * ID / date) sits above compact Prepared for / Secondary / Job Address / Prepared by rows. The Secondary
 * row only appears once the estimate has a secondary contact.
 *
 * Every row here opens {@link QuoteDocumentHeaderMobileSheet} rather than expanding in place — a row can
 * only fit one truncated value, so the sheet is where each section's real content becomes readable and
 * tappable, with its edit actions listed below. The summary row opens the document sheet (logo, company
 * name, address, phone, dates); a compact row with a value opens its own. A compact row with no value has
 * nothing to show in a sheet, so it displays the prompt to add one and goes straight to that section's
 * picker.
 */
export default function QuoteDocumentHeaderMobile({ flush, data, onOpenSection }: QuoteDocumentHeaderMobileProps) {
  const classes = { ...useQuoteDocumentHeaderStyles(), ...useSummaryStyles() }
  const {
    onEditJobAddress,
    onSelectEstimator,
    isTemplate,
    isWorkOrder,
    docLabel,
    quoteId,
    collapsedDate,
    contactName,
    contactCompany,
    contactClickable,
    hasContact,
    activateContact,
    secondaryContactName,
    secondaryContactCompany,
    secondaryContactEmails,
    hasSecondaryContact,
    jobIdentifier,
    jobAddressLines,
    jobAddressClickable,
    hasJobAddress,
    showJobAddress,
    owner,
    ownerUser,
    userPictures,
    estimatorName,
    estimatorClickable,
    hasEstimator,
    showEstimator
  } = data

  // One compact row: icon (or avatar), muted label, value. Activating it opens that section's sheet, or
  // — for a row with no value yet — that section's picker.
  const renderCompactRow = (args: {
    key: string
    icon: React.ComponentType<{ className?: string }>
    avatar?: React.ReactNode
    label: string
    value?: string | null
    emptyLabel?: string
    clickable: boolean
    onActivate?: (element: HTMLElement) => void
  }) => {
    const { key, icon: Icon, avatar, label, value, emptyLabel, clickable, onActivate } = args
    // With no value and no way to add one there's nothing to show, so the row is dropped entirely.
    if (!value && !emptyLabel) return null
    if (!value && !clickable) return null
    return (
      <div
        key={key}
        className={classnames(classes.compactRow, { [classes.compactRowClickable]: clickable })}
        {...activatable(clickable && !!onActivate, (element) => onActivate(element))}
      >
        {avatar ?? <Icon className={classes.compactIcon} />}
        <Typography component="span" className={classes.compactLabel}>
          {label}
        </Typography>
        <Typography
          component="span"
          className={classnames(classes.compactValue, { [classes.compactEmptyValue]: !value })}
        >
          {value || emptyLabel}
        </Typography>
      </div>
    )
  }

  return (
    <div className={[classes.root, flush && classes.flush].filter(Boolean).join(' ')}>
      {/* One target for the whole row: it opens the document sheet (logo / company name / address /
          phone / dates), and the date is edited from that sheet's "Edit Created Date" action rather than
          by tapping the date here — two targets in one row made it unclear which did what. */}
      <div
        className={classnames(classes.summaryRow, classes.summaryRowClickable)}
        {...activatable(true, () => onOpenSection('document'))}
      >
        <div className={classes.summaryLeft}>
          <Typography component="div" className={classes.summaryLabel}>
            {docLabel}
          </Typography>
          {quoteId && (
            <Typography component="div" className={classes.summaryMuted}>
              ID {quoteId}
            </Typography>
          )}
        </div>
        <div className={classes.summaryRight}>
          {/* A template carries no issue date — see the desktop header's note. */}
          {/* A work order's "Issued:" date reads a shade darker than an estimate's, as in the prototype;
              the caret says the row opens a sheet. */}
          {collapsedDate && !isTemplate && (
            <Typography
              component="div"
              className={classnames(classes.summaryMuted, { [classes.summaryIssued]: isWorkOrder })}
            >
              {collapsedDate}
            </Typography>
          )}
          <ArrowDropDownIcon className={classes.summaryCaret} />
        </div>
      </div>

      {/* A template's contact, address and estimator are filled on the estimates made from it, so the
          rows read as inactive placeholders rather than "Add Contact" prompts (ENG-1282). */}
      {!isWorkOrder && isTemplate && (
        <>
          {TEMPLATE_ROWS.map(({ key, icon, label }) =>
            renderCompactRow({ key, icon, label, value: null, emptyLabel: 'Filled on Estimate', clickable: false })
          )}
        </>
      )}

      {!isWorkOrder && !isTemplate && (
        <>
          {/* A row with a value opens the detail sheet; an empty one has nothing to show there, so it
              goes straight to the picker (the same jump `activateContact` already makes). A row stays
              clickable when it only has a value to read — the sheet is useful without edit rights. */}
          {renderCompactRow({
            key: 'contact',
            icon: PersonIcon,
            label: 'Prepared for',
            // a company contact leads with the company, as the desktop column does (ENG-2033)
            value: contactCompany || contactName,
            emptyLabel: 'Add Contact',
            clickable: hasContact || contactClickable,
            onActivate: hasContact ? () => onOpenSection('contact') : activateContact
          })}
          {/* Only once a secondary contact exists — it is added from the "Prepared for" sheet. */}
          {hasSecondaryContact &&
            renderCompactRow({
              key: 'secondary-contact',
              icon: PersonIcon,
              label: 'Secondary',
              // A contact saved with no name still has to be reachable, so fall back to its email.
              value: secondaryContactCompany || secondaryContactName || secondaryContactEmails[0],
              clickable: true,
              onActivate: () => onOpenSection('secondary-contact')
            })}
          {/* Hidden for read-only viewers when there is no address to show (ENG-2037). */}
          {showJobAddress &&
            renderCompactRow({
              key: 'job-address',
              icon: LocationOnIcon,
              label: 'Job Address',
              value: jobIdentifier || jobAddressLines[0] || null,
              emptyLabel: 'Add Address',
              clickable: hasJobAddress || jobAddressClickable,
              onActivate: hasJobAddress ? () => onOpenSection('job') : () => onEditJobAddress()
            })}
          {/* Hidden for read-only viewers when no estimator is assigned (ENG-2209). */}
          {showEstimator &&
            renderCompactRow({
              key: 'estimator',
              icon: PeopleIcon,
              avatar: owner ? (
                <EstimatorAvatar
                  user={{ id: owner.id, firstName: owner.firstName, lastName: owner.lastName }}
                  userPictures={userPictures}
                  width="20px"
                  height="20px"
                  renderEmpty="blank"
                  hideTooltip
                  avatarColor={getActivityColor(ownerUser?.user_metadata?.color)}
                />
              ) : undefined,
              label: 'Prepared by',
              value: estimatorName,
              emptyLabel: 'Assign Estimator',
              clickable: hasEstimator || estimatorClickable,
              onActivate: hasEstimator ? () => onOpenSection('estimator') : () => onSelectEstimator()
            })}
        </>
      )}
    </div>
  )
}
